import React, { useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { UserDtoProfileEnum } from 'lib_api/lib/api/gen';

import FAQAdmin from 'components/FAQ/FAQAdmin';
import NavBar from 'components/NavBar';
import { TableSort } from 'components/WrappedComponents/Table/types';
import AdminFonctionnelFilterProvider from 'hooks/AdminFonctionnelFilterStoreContext';
import { SortOptions } from 'hooks/dossiers/useSearchDossierSummaries';
import { useEnvironnement } from 'hooks/EnvironementStoreContext';
import PaginationProvider, {
  DictTablePagination,
  PAGINATION_DICT_DEFAULT,
} from 'hooks/PaginationStoreContext';
import SortProvider from 'hooks/SortStoreContext';
import { useActionsRequises } from 'hooks/useActionsRequises';
import { NOT_FOUND_PATH, profileEnumMapping } from 'utils/enumData';

import PageAdministrationAvancee from './AdministrationAvancee/PageAdministrationAvancee';
import PageAdministration from './Comptes/Administration/PageAdministration';
import PageSupervision from './Comptes/Supervision/PageSupervision';
import DashboardConfigurationTable from './Configuration/Dashboard/DashboardConfigurationTable';
import { DashboardAdminFonctionnelFilter } from './Dashboard/types';
import DashboardFluxAutoriteFourriere from './Flux/alienation/AutoritesFourriere/DashboardFluxAutoriteFourriere';
import DashboardWarningsAutoriteFourriere from './Flux/alienation/AutoritesFourriere/Details/DashboardWarnings/DashboardWarningsAutoriteFourriere';
import DashboardSynchroLieuxDepot from './Flux/alienation/LieuxDepot/DashboardSynchroLieuxDepot';
import DashboardWarningsLieuxDepot from './Flux/alienation/LieuxDepot/details/DashboardWarnings/DashboardWarningsLieuxDepot';
import DashboardVenteDossiers from './Flux/alienation/VenteDossiers/DashboardVenteDossiers';
import DashboardWarningsVenteDossier from './Flux/alienation/VenteDossiers/Details/DashboardWarnings/DashboardWarningsVenteDossier';
import DashboardEvenementsAntai from './Flux/antai/DashboardEvenementsAntai';
import DashboardWarningsAntai from './Flux/antai/DashboardWarnings/DashboardWarningsAntai';
import DashboardImportsRpsi from './Flux/rpsi/DashboardImportsRpsi';
import {
  DashboardAutoriteFourriere,
  DashboardUniteFo,
} from './Referentiels/Dashboards';
import DashboardBranche from './Referentiels/Dashboards/Branche/DashboardBranche';
import DashboardCentreVHU from './Referentiels/Dashboards/CentreVHU/DashboardCentreVHU';
import DashboardEtat from './Referentiels/Dashboards/Etat/DashboardEtat';
import DashboardFaq from './Referentiels/Dashboards/Faq/DashboardFaq';
import DashboardFaqDomaine from './Referentiels/Dashboards/FaqDomaine/DashboardFaqDomaine';
import DashboardFooterLink from './Referentiels/Dashboards/FooterLink/DashboardFooterLink';
import DashboardFourriere from './Referentiels/Dashboards/Fourriere/DashboardFourriere';
import DashboardGenreSimplifie from './Referentiels/Dashboards/GenreSimplifie/DashboardGenreSimplifie';
import DashboardJuridiction from './Referentiels/Dashboards/Juridiction/DashboardJuridiction';
import DashboardMarque from './Referentiels/Dashboards/Marque/DashboardMarque';
import DashboardMotifMef from './Referentiels/Dashboards/MotifMef/DashboardMotifMef';
import DashboardMotifSortie from './Referentiels/Dashboards/MotifSortie/DashboardMotifSortie';
import DashboardTypeAf from './Referentiels/Dashboards/TypeAf/DashboardTypeAf';
import DashboardVideos from './Referentiels/Dashboards/Videos/DashboardVideos';
import DocumentsSpecifique from './Referentiels/DocumentsSpecifique';
import ReferentielExport from './Referentiels/Export';
import FicheDescriptive from './Referentiels/FicheDescriptive';
import InformationMessage from './Referentiels/InformationMessage';
import Manuels from './Referentiels/Manuels';
import { useAdminNavbarContent } from './adminNavbarContent';
import Classement from './Classement';
import Dashboard from './Dashboard';
import RoutesDomaine from './Domaine';
import PageDossier from './PageDossier';
import useActiveAlienationSolution from './useActiveAlienationSolution';

const AdminRouter: React.FC = () => {
  const [pagination, setPagination] = useState<DictTablePagination>(
    PAGINATION_DICT_DEFAULT,
  );
  const [filter, setFilter] = useState<
    Partial<DashboardAdminFonctionnelFilter>
  >({});
  const [sort, setSort] = useState<Partial<TableSort<SortOptions>>>({
    IMMATRICULATION: true,
  });
  const adminProfileData =
    profileEnumMapping[UserDtoProfileEnum.ADMIN_FONCTIONNEL];
  const activeSolution = useActiveAlienationSolution();
  const navbarContent = useAdminNavbarContent(
    adminProfileData.homePath,
    activeSolution,
  );

  const adminActionsRequises = useActionsRequises();

  const [{ JUSTICE_ACTIVE_BOOL }] = useEnvironnement();

  return (
    <PaginationProvider
      value={{ pagination: pagination, setPagination: setPagination }}
    >
      <AdminFonctionnelFilterProvider
        value={{
          filter: filter,
          setFilter: setFilter,
          actionsRequises: adminActionsRequises,
        }}
      >
        <SortProvider value={{ sort: sort, setSort: setSort }}>
          <NavBar items={navbarContent} />
          <Routes>
            <Route path="" element={<Dashboard />} />
            <Route path="referentiels">
              <Route
                path="documents-specifiques"
                element={<DocumentsSpecifique />}
              />
              <Route path="export" element={<ReferentielExport />} />
              <Route path="manuels" element={<Manuels />} />
              <Route path="fiche-descriptive" element={<FicheDescriptive />} />
              <Route
                path="information-message"
                element={<InformationMessage />}
              />
              <Route path="dashboard">
                <Route path="videos" element={<DashboardVideos />} />
                <Route path="branche" element={<DashboardBranche />} />
                <Route path="etat" element={<DashboardEtat />} />
                <Route
                  path="genre-simplifie"
                  element={<DashboardGenreSimplifie />}
                />
                <Route path="unite-fo" element={<DashboardUniteFo />} />
                <Route path="fourriere" element={<DashboardFourriere />} />
                {JUSTICE_ACTIVE_BOOL ? (
                  <Route
                    path="juridiction"
                    element={<DashboardJuridiction />}
                  />
                ) : (
                  <></>
                )}
                <Route
                  path="autorite-fourriere"
                  element={<DashboardAutoriteFourriere />}
                />
                <Route
                  path="type-autorite-fourriere"
                  element={<DashboardTypeAf />}
                />
                <Route
                  path="lien-pied-de-page"
                  element={<DashboardFooterLink />}
                />
                <Route path="marque" element={<DashboardMarque />} />
                <Route path="motif-mef" element={<DashboardMotifMef />} />
                <Route path="motif-sortie" element={<DashboardMotifSortie />} />
                <Route path="centre-vhu" element={<DashboardCentreVHU />} />
                <Route path="faq-domaine" element={<DashboardFaqDomaine />} />
                <Route path="faq">
                  <Route path="" element={<DashboardFaq />} />
                  <Route path="view/:profile" element={<FAQAdmin />} />
                </Route>
              </Route>
            </Route>
            <Route path="comptes">
              <Route path="administration" element={<PageAdministration />} />
              <Route path="supervision" element={<PageSupervision />} />
            </Route>
            <Route path="domaine/*" element={<RoutesDomaine />} />

            <Route path="classement" element={<Classement />} />

            <Route
              path="configurations"
              element={<DashboardConfigurationTable />}
            />

            <Route path="fluxs">
              <Route
                path="vente-dossiers"
                element={<DashboardVenteDossiers />}
              />
              <Route
                path="vente-dossiers/:id"
                element={<DashboardWarningsVenteDossier />}
              />
              <Route
                path="autorite-fourriere"
                element={<DashboardFluxAutoriteFourriere />}
              />
              <Route
                path="autorite-fourriere/:id"
                element={<DashboardWarningsAutoriteFourriere />}
              />
              <Route path="import-rpsi" element={<DashboardImportsRpsi />} />
              <Route
                path="evenements-antai"
                element={<DashboardEvenementsAntai />}
              />
              <Route
                path="evenements-antai/:id"
                element={<DashboardWarningsAntai />}
              />
              <Route
                path="lieux-depot"
                element={<DashboardSynchroLieuxDepot />}
              />
              <Route
                path="lieux-depot/:id"
                element={<DashboardWarningsLieuxDepot />}
              />
            </Route>
            <Route path="dossier/:id" element={<PageDossier />} />

            <Route
              path="administration-avancee"
              element={<PageAdministrationAvancee />}
            />
            <Route
              path="*"
              element={<Navigate replace to={NOT_FOUND_PATH} />}
            />
          </Routes>
        </SortProvider>
      </AdminFonctionnelFilterProvider>
    </PaginationProvider>
  );
};

export default AdminRouter;
